import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { getFileDownloadUrl } from '../utils/api';
import styles from './FileViewer.module.css';

// In-app viewer for files uploaded under the Files page. Type-dispatches to
// a sub-renderer based on the file's mime type / extension. v1 supports:
//
//   • PDF   — paged scroll view with content search (next/prev match)
//   • Image — img with object-fit: contain
//   • Text  — fetched + rendered in a <pre>
//
// Anything else falls through to a download prompt. We deliberately avoid
// audio/video for now (out of scope per the original ask).

// pdfjs worker is also configured by PdfViewer.jsx; this set is idempotent.
if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url
  ).toString();
}

function escapeHtml(s) {
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

const TEXT_EXT = new Set([
  'txt', 'md', 'csv', 'log', 'json', 'xml', 'yaml', 'yml',
  'ini', 'env', 'conf', 'js', 'jsx', 'ts', 'tsx', 'css', 'html',
  'sql', 'sh', 'bat', 'ps1',
]);
const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif']);

function pickViewer(file) {
  const mime = (file.mime_type || '').toLowerCase();
  const ext  = (file.name.split('.').pop() || '').toLowerCase();
  if (mime === 'application/pdf' || ext === 'pdf')                       return 'pdf';
  if (mime.startsWith('image/')   || IMAGE_EXT.has(ext))                 return 'image';
  if (mime.startsWith('text/')    || TEXT_EXT.has(ext) || mime.includes('json') || mime.includes('xml')) return 'text';
  return 'fallback';
}

export default function FileViewer({ file, onClose }) {
  const kind = pickViewer(file);
  const backdropRef = useRef(null);

  // Esc closes the viewer.
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Stop wheel + touch events from bubbling out to PdfViewer's NATIVE
  // listeners behind the modal — otherwise scrolling/zooming inside the
  // file viewer also operates on the drawing it was opened from. Pointer
  // events (pinch/pan) are blocked via React props on the JSX below; wheel
  // has to be a native listener because PdfViewer registers its wheel
  // handler with addEventListener (so React stopPropagation alone wouldn't
  // reach it).
  useEffect(() => {
    const el = backdropRef.current;
    if (!el) return;
    const stop = (e) => e.stopPropagation();
    const opts = { passive: false };
    el.addEventListener('wheel',      stop, opts);
    el.addEventListener('touchstart', stop, opts);
    el.addEventListener('touchmove',  stop, opts);
    el.addEventListener('touchend',   stop, opts);
    return () => {
      el.removeEventListener('wheel',      stop, opts);
      el.removeEventListener('touchstart', stop, opts);
      el.removeEventListener('touchmove',  stop, opts);
      el.removeEventListener('touchend',   stop, opts);
    };
  }, []);

  // Portal to <body> so the modal escapes any transformed ancestor.
  // MarkupCanvas mounts FileViewer inside PdfViewer's `.page` div, which
  // has `transform: translate(...)` for pan/zoom — and a transformed
  // ancestor breaks `position: fixed`, leaving the modal anchored to the
  // panned page instead of the viewport. Portal moves it to body so
  // `position: fixed; inset: 0` actually fills the viewport.
  return ReactDOM.createPortal(
    <div
      ref={backdropRef}
      className={styles.backdrop}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onPointerCancel={(e) => e.stopPropagation()}
    >
      <div className={styles.viewer}>
        <header className={styles.header}>
          <span className={styles.title} title={file.name}>{file.name}</span>
          <a
            href={getFileDownloadUrl(file.id, { download: true })}
            className={styles.iconBtn}
            title="Download"
            download={file.name}
          >
            <DownloadIcon />
          </a>
          <button className={styles.iconBtn} onClick={onClose} title="Close (Esc)" aria-label="Close">
            ×
          </button>
        </header>
        <div className={styles.body}>
          {kind === 'pdf'      && <PdfView      file={file} />}
          {kind === 'image'    && <ImageView    file={file} />}
          {kind === 'text'     && <TextView     file={file} />}
          {kind === 'fallback' && <FallbackView file={file} />}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── PDF view with search ─────────────────────────────────────────────────────
// Lazy-rendered paged scroll viewer:
//   • Doc loads → page-size pre-pass (cheap) so wrappers can be sized correctly
//     before any canvas is allocated
//   • IntersectionObserver kicks off canvas render only as a page nears the
//     viewport (with a 500px lead margin). Caps the allocation pressure that
//     killed large PDFs in the eager-render version.
//   • Text extraction runs sequentially in the background so memory stays
//     bounded; search becomes available incrementally as more pages index
//   • iOS canvas-size cap (14 MP / 4096 edge) applied per page
//
// Page-level match highlighting only — character-precise highlight needs a
// textLayer overlay; deferred until users actually ask.
function PdfView({ file }) {
  const [doc,            setDoc]            = useState(null);
  const [numPages,       setNumPages]       = useState(0);
  // [{ w, h }] in PDF points. Defaulted to letter so the layout doesn't
  // collapse before sizes are known; refined per-page once available.
  const [pageSizes,      setPageSizes]      = useState([]);
  const [loadError,      setLoadError]      = useState(null);

  const [pageTexts,      setPageTexts]      = useState([]);  // string per page
  const [indexedCount,   setIndexedCount]   = useState(0);   // # pages with text extracted

  const [query,          setQuery]          = useState('');
  const [matches,        setMatches]        = useState([]);  // [{ page, charIndex }]
  const [currentMatch,   setCurrentMatch]   = useState(-1);

  const containerRef     = useRef(null);
  const pageRefs         = useRef([]);              // wrappers
  const renderedPages    = useRef(new Set());       // page numbers (canvas) rendered
  const renderingPages   = useRef(new Set());       // page numbers in-flight
  const textLayerReady   = useRef(new Set());       // page numbers w/ text layer painted
  const observerRef      = useRef(null);
  // Per-page RenderTask handle so we can cancel mid-flight renders when the
  // user navigates away or asks to re-render.
  const renderTasks      = useRef(new Map());       // pageNo → pdfjs RenderTask
  // Indexing pauses while user-driven renders are pending so the pdfjs
  // worker isn't starved by background text extraction.
  const pendingUserRenders = useRef(0);
  const queryRef         = useRef('');

  const url = getFileDownloadUrl(file.id);

  // Detect iOS once; used for both render scale and DPR cap.
  const isIOS = useRef(
    /iPad|iPhone|iPod/.test(navigator.userAgent || '') ||
    (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent || ''))
  ).current;

  // ── Stage 1: load doc + fetch all page sizes ──
  // Page-size lookup is cheap (pdfjs already parsed the cross-ref); doing it
  // sequentially keeps memory predictable on huge docs.
  useEffect(() => {
    let cancelled = false;
    pdfjsLib.getDocument(url).promise
      .then(async (pdf) => {
        if (cancelled) return;
        setDoc(pdf);
        setNumPages(pdf.numPages);
        const sizes = new Array(pdf.numPages).fill(0).map(() => ({ w: 612, h: 792 }));
        setPageSizes(sizes); // immediate defaults so wrappers have a size
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          try {
            const page = await pdf.getPage(i);
            const vp   = page.getViewport({ scale: 1 });
            sizes[i - 1] = { w: vp.width, h: vp.height };
          } catch (_) { /* keep default */ }
          // Periodically flush so the layout updates as we go without
          // re-rendering on every single page.
          if (i % 25 === 0 && !cancelled) setPageSizes([...sizes]);
        }
        if (!cancelled) setPageSizes([...sizes]);
      })
      .catch((err) => { if (!cancelled) setLoadError(err.message || 'Failed to load PDF'); });
    return () => { cancelled = true; };
  }, [url]);

  // ── Stage 2: extract text in background, sequentially ──
  // Updates pageTexts incrementally so search lights up as soon as the
  // user's matching page has been indexed. The loop yields between
  // pages and pauses entirely while user-driven renders are pending so
  // the pdfjs worker isn't starved.
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    const texts = new Array(doc.numPages).fill('');
    setPageTexts(texts);
    setIndexedCount(0);
    (async () => {
      for (let i = 1; i <= doc.numPages; i++) {
        if (cancelled) return;
        // Wait while user-driven renders are queued — they need the
        // worker more than the background indexer does.
        while (pendingUserRenders.current > 0 && !cancelled) {
          await new Promise((r) => setTimeout(r, 100));
        }
        try {
          const page = await doc.getPage(i);
          const tc   = await page.getTextContent();
          texts[i - 1] = tc.items.map((it) => it.str).join(' ');
        } catch (_) {
          texts[i - 1] = '';
        }
        if (!cancelled) {
          // Mutate a fresh array so React notices the change. Throttle
          // updates: notify React every 5 pages and at the end.
          if (i % 5 === 0 || i === doc.numPages) setPageTexts(texts.slice());
          setIndexedCount(i);
        }
        // Yield to the event loop so any queued user renders can dispatch.
        await new Promise((r) => setTimeout(r, 0));
      }
    })();
    return () => { cancelled = true; };
  }, [doc]);

  // ── Stage 3: lazy canvas render on viewport intersection ──
  // Pages start as empty canvases inside correctly-sized wrappers. When a
  // wrapper enters (or comes within 500px of) the scroll viewport, kick off
  // its render. Already-rendered canvases are kept in memory — for typical
  // documents the cumulative bytes are far less than rendering all eagerly.
  useEffect(() => {
    if (!doc || !numPages || !containerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const idx = Number(entry.target.getAttribute('data-page-idx'));
          if (Number.isFinite(idx)) renderPageCanvas(idx);
        }
      },
      {
        root: containerRef.current,
        rootMargin: '500px 0px 500px 0px', // pre-render just-out-of-view neighbors
        threshold: 0,
      }
    );
    observerRef.current = observer;
    // Observe everything once. New pageRefs as numPages changes are picked
    // up by the second useEffect below.
    for (const el of pageRefs.current) {
      if (el) observer.observe(el);
    }
    return () => { observer.disconnect(); observerRef.current = null; };
  }, [doc, numPages]); // eslint-disable-line

  // Cancel + clear state for a page so the next call re-renders fresh.
  function resetPageState(idx) {
    const pageNo = idx + 1;
    const task = renderTasks.current.get(pageNo);
    if (task) {
      try { task.cancel(); } catch (_) {}
      renderTasks.current.delete(pageNo);
    }
    renderingPages.current.delete(pageNo);
    renderedPages.current.delete(pageNo);
    textLayerReady.current.delete(pageNo);
    const wrapper = pageRefs.current[idx];
    if (wrapper) wrapper.classList.remove(styles.pageRendered);
  }

  // Force-render a page: cancel any in-flight render for it, clear state,
  // and re-render. Use this for user-driven navigation so we always get
  // the page back even if a previous render hung or was cancelled.
  async function forceRenderPage(idx) {
    pendingUserRenders.current += 1;
    resetPageState(idx);
    try { await renderPageCanvas(idx); }
    finally { pendingUserRenders.current = Math.max(0, pendingUserRenders.current - 1); }
  }

  async function renderPageCanvas(idx) {
    if (!doc) return;
    const pageNo = idx + 1;
    if (renderedPages.current.has(pageNo) || renderingPages.current.has(pageNo)) return;
    renderingPages.current.add(pageNo);
    try {
      const page    = await doc.getPage(pageNo);
      const native  = page.getViewport({ scale: 1 });
      let renderScale = 1.25;
      let dpr         = isIOS ? 1 : (window.devicePixelRatio || 1);
      let total       = renderScale * dpr;
      const MAX_EDGE = 4096;
      const MAX_AREA = 14_000_000;
      if (Math.max(native.width, native.height) * total > MAX_EDGE) {
        total = MAX_EDGE / Math.max(native.width, native.height);
      }
      if (native.width * total * native.height * total > MAX_AREA) {
        total = Math.sqrt(MAX_AREA / (native.width * native.height));
      }
      const wrapper = pageRefs.current[idx];
      if (!wrapper) return;
      const canvas = wrapper.querySelector('canvas');
      if (!canvas) return;
      const vp = page.getViewport({ scale: total });
      canvas.width  = vp.width;
      canvas.height = vp.height;
      canvas.style.width  = `${native.width  * renderScale}px`;
      canvas.style.height = `${native.height * renderScale}px`;
      const renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
      renderTasks.current.set(pageNo, renderTask);
      await renderTask.promise;
      renderTasks.current.delete(pageNo);
      renderedPages.current.add(pageNo);
      // Mark the wrapper rendered so the placeholder text disappears via CSS.
      wrapper.classList.add(styles.pageRendered);

      // Render text layer for selectable text + search highlights. Uses a
      // viewport at the same display scale as the canvas (renderScale) so
      // text spans align with the rasterized image. Best-effort: if pdfjs
      // doesn't expose renderTextLayer in this build, the canvas still
      // works without highlights.
      const textLayerDiv = wrapper.querySelector(`.${styles.textLayer}`);
      if (textLayerDiv && pdfjsLib.renderTextLayer) {
        try {
          const textContent = await page.getTextContent();
          const displayVp   = page.getViewport({ scale: renderScale });
          textLayerDiv.innerHTML = '';
          const renderOpts = {
            container: textLayerDiv,
            viewport:  displayVp,
            textDivs:  [],
          };
          // pdfjs-dist 3.x renamed `textContent` → `textContentSource`;
          // pass both so we work either way.
          renderOpts.textContent       = textContent;
          renderOpts.textContentSource = textContent;
          await pdfjsLib.renderTextLayer(renderOpts).promise;
          // Stash the original text per span so highlight passes can be
          // re-applied + cleared cheaply.
          for (const span of textLayerDiv.querySelectorAll('span')) {
            if (span.childNodes.length === 1 && span.firstChild.nodeType === 3) {
              span.dataset.text = span.textContent;
            }
          }
          textLayerReady.current.add(pageNo);
          // Apply current query if any.
          applyHighlightsToPage(idx);
        } catch (_) { /* textLayer optional */ }
      }
    } catch (err) {
      // Page-level failure — log but don't take down the whole viewer.
      // (Most common: cancelled render task on rapid scroll.)
      renderTasks.current.delete(pageNo);
      const wrapper = pageRefs.current[idx];
      if (wrapper && err?.name !== 'RenderingCancelledException') {
        wrapper.dataset.error = err.message || 'render failed';
      }
    } finally {
      renderingPages.current.delete(pageNo);
    }
  }

  // ── Apply highlights to a single page's textLayer ──
  // Walks each text span, splits its content around any matches of the
  // current query, and wraps them in <mark>. Re-applying is cheap because
  // we cache the original text in span.dataset.text.
  function applyHighlightsToPage(idx) {
    const wrapper = pageRefs.current[idx];
    if (!wrapper) return;
    const tl = wrapper.querySelector(`.${styles.textLayer}`);
    if (!tl) return;
    const q = queryRef.current.trim().toLowerCase();
    for (const span of tl.querySelectorAll('span')) {
      const original = span.dataset.text;
      if (original == null) continue;
      if (!q) {
        // Clear: restore original text.
        if (span.firstChild?.nodeType !== 3 || span.firstChild.nodeValue !== original) {
          span.textContent = original;
        }
        continue;
      }
      const lower = original.toLowerCase();
      if (lower.indexOf(q) === -1) {
        if (span.firstChild?.nodeType !== 3 || span.firstChild.nodeValue !== original) {
          span.textContent = original;
        }
        continue;
      }
      // Build innerHTML with <mark> around every occurrence.
      let html = '';
      let from = 0;
      while (true) {
        const found = lower.indexOf(q, from);
        if (found === -1) {
          html += escapeHtml(original.slice(from));
          break;
        }
        html += escapeHtml(original.slice(from, found));
        html += `<mark class="${styles.searchMark}">${escapeHtml(original.slice(found, found + q.length))}</mark>`;
        from = found + q.length;
      }
      span.innerHTML = html;
    }
  }

  // ── Apply / clear active-match emphasis ──
  // The currently-selected match gets a stronger fill via the
  // .searchMarkActive class. Walks every rendered page once, counts marks
  // from the top, and tags the global N-th match.
  function refreshActiveMark() {
    const activeIdx = currentMatch;
    let counter = 0;
    for (let i = 0; i < numPages; i++) {
      const wrapper = pageRefs.current[i];
      if (!wrapper) continue;
      const marks = wrapper.querySelectorAll(`mark.${styles.searchMark}`);
      for (const m of marks) {
        if (counter === activeIdx) m.classList.add(styles.searchMarkActive);
        else                       m.classList.remove(styles.searchMarkActive);
        counter += 1;
      }
    }
  }

  // ── Search recompute ──
  useEffect(() => {
    queryRef.current = query;
    if (!query.trim() || !pageTexts.length) {
      setMatches([]);
      setCurrentMatch(-1);
      // Clear marks on every rendered page.
      for (let i = 0; i < numPages; i++) applyHighlightsToPage(i);
      return;
    }
    const q   = query.trim().toLowerCase();
    const out = [];
    pageTexts.forEach((text, i) => {
      if (!text) return;
      const lower = text.toLowerCase();
      let from = 0;
      while (true) {
        const idx = lower.indexOf(q, from);
        if (idx === -1) break;
        out.push({ page: i + 1, charIndex: idx });
        from = idx + q.length;
      }
    });
    setMatches(out);
    // Keep the current selection if still valid; otherwise reset to 0.
    setCurrentMatch((prev) => (out.length === 0 ? -1 : (prev >= 0 && prev < out.length ? prev : 0)));
    // Re-apply highlights on every rendered page.
    for (let i = 0; i < numPages; i++) applyHighlightsToPage(i);
    // eslint-disable-next-line
  }, [query, pageTexts, numPages]);

  // ── Scroll to current match's page + force-render it ──
  // Force-render: cancel any in-flight render for this page (the indexer
  // or a stale prior render), clear state, and re-render. Bumps
  // pendingUserRenders so the indexer pauses while we paint, freeing
  // the pdfjs worker. Skip neighbor pre-rendering — extra worker
  // contention on a 700+ page doc made navigation feel worse.
  useEffect(() => {
    if (currentMatch < 0) return;
    const m = matches[currentMatch];
    if (!m) return;
    const pageIdx = m.page - 1;
    const pageEl  = pageRefs.current[pageIdx];
    if (pageEl?.scrollIntoView) {
      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    forceRenderPage(pageIdx).then(() => {
      // Update active highlight once the page's textLayer has had a
      // chance to render.
      setTimeout(refreshActiveMark, 50);
    });
    // eslint-disable-next-line
  }, [currentMatch, matches]);

  function nextMatch() {
    if (!matches.length) return;
    setCurrentMatch((i) => (i + 1) % matches.length);
  }
  function prevMatch() {
    if (!matches.length) return;
    setCurrentMatch((i) => (i - 1 + matches.length) % matches.length);
  }

  if (loadError) {
    return <div className={styles.errorBox}>Could not load PDF: {loadError}</div>;
  }
  if (!doc) {
    return <div className={styles.loadingBox}>Loading PDF…</div>;
  }

  const indexing = indexedCount < numPages;

  return (
    <div className={styles.pdfWrap}>
      <div className={styles.pdfToolbar}>
        <div className={styles.searchWrap}>
          <SearchIcon />
          <input
            className={styles.searchInput}
            type="search"
            placeholder={indexing ? `Indexing… ${indexedCount}/${numPages}` : 'Search in document…'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.shiftKey ? prevMatch() : nextMatch();
              }
            }}
          />
        </div>
        <div className={styles.searchNav}>
          <span className={styles.searchCount}>
            {query.trim()
              ? matches.length === 0
                ? (indexing ? 'Indexing…' : 'No matches')
                : `${currentMatch + 1} / ${matches.length}${indexing ? '+' : ''}`
              : `${numPages} page${numPages === 1 ? '' : 's'}`}
          </span>
          <button
            className={styles.searchNavBtn}
            onClick={prevMatch}
            disabled={!matches.length}
            title="Previous match (Shift+Enter)"
            aria-label="Previous match"
          ><ChevronUp /></button>
          <button
            className={styles.searchNavBtn}
            onClick={nextMatch}
            disabled={!matches.length}
            title="Next match (Enter)"
            aria-label="Next match"
          ><ChevronDown /></button>
        </div>
      </div>
      <div className={styles.pdfPages} ref={containerRef}>
        {Array.from({ length: numPages }, (_, i) => {
          const pageNo = i + 1;
          const matchPages = matches.filter((m) => m.page === pageNo).length;
          const isCurrentPage = currentMatch >= 0 && matches[currentMatch]?.page === pageNo;
          const sz = pageSizes[i] || { w: 612, h: 792 };
          // Wrapper sized to the page's intrinsic dimensions × renderScale
          // so layout is stable even before the canvas paints.
          const wrapW = sz.w * 1.25;
          const wrapH = sz.h * 1.25;
          return (
            <div
              key={pageNo}
              data-page-idx={i}
              ref={(el) => {
                pageRefs.current[i] = el;
                // If the observer already exists, observe the new ref so
                // dynamically-added pages are caught.
                if (el && observerRef.current) observerRef.current.observe(el);
              }}
              className={`${styles.pdfPageWrap} ${isCurrentPage ? styles.pdfPageHit : ''}`}
              style={{ width: wrapW, height: wrapH }}
            >
              <canvas className={styles.pdfPageCanvas} />
              {/* Selectable + searchable text layer. pdfjs.renderTextLayer
                  populates this with absolute-positioned spans that align
                  with the canvas raster underneath. */}
              <div className={styles.textLayer} aria-hidden="true" />
              <div className={styles.pdfPagePlaceholder}>Page {pageNo}</div>
              <div className={styles.pdfPageLabel}>
                <span>Page {pageNo} / {numPages}</span>
                {query.trim() && matchPages > 0 && (
                  <span className={styles.pdfPageMatchCount}>
                    {matchPages} match{matchPages === 1 ? '' : 'es'}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Image viewer ─────────────────────────────────────────────────────────────
// Object-fit: contain inside a scrollable wrapper so portrait + landscape
// + huge images all render reasonably. No pinch-zoom on this version —
// the user can open the file in a new tab via download for full controls.
function ImageView({ file }) {
  const [error, setError] = useState(false);
  if (error) return <div className={styles.errorBox}>Could not load image.</div>;
  return (
    <div className={styles.imageWrap}>
      <img
        src={getFileDownloadUrl(file.id)}
        alt={file.name}
        className={styles.image}
        onError={() => setError(true)}
      />
    </div>
  );
}

// ── Text viewer ──────────────────────────────────────────────────────────────
// Fetches the file as text, displays in a monospace <pre>. Caps load at 1 MB
// so a giant log file can't lock up the modal — show a download prompt instead.
function TextView({ file }) {
  const [text,    setText]    = useState(null);
  const [error,   setError]   = useState(null);
  const [tooBig,  setTooBig]  = useState(false);

  const MAX_BYTES = 1_000_000;

  useEffect(() => {
    let cancelled = false;
    if ((file.size || 0) > MAX_BYTES) { setTooBig(true); return; }
    fetch(getFileDownloadUrl(file.id))
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => { if (!cancelled) setText(t); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load text'); });
    return () => { cancelled = true; };
  }, [file.id, file.size]);

  if (tooBig) {
    return (
      <div className={styles.errorBox}>
        File is too large for inline preview ({Math.round((file.size || 0) / 1024)} KB).
        Use the download button above to open it.
      </div>
    );
  }
  if (error) return <div className={styles.errorBox}>Could not load: {error}</div>;
  if (text === null) return <div className={styles.loadingBox}>Loading…</div>;
  return <pre className={styles.textPre}>{text}</pre>;
}

// ── Fallback ─────────────────────────────────────────────────────────────────
function FallbackView({ file }) {
  return (
    <div className={styles.fallbackBox}>
      <p className={styles.fallbackHead}>No inline preview for this file type.</p>
      <p className={styles.fallbackSub}>{file.mime_type || 'unknown type'}</p>
      <a
        href={getFileDownloadUrl(file.id, { download: true })}
        className={styles.fallbackBtn}
        download={file.name}
      >
        Download
      </a>
    </div>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 7l3 3 3-3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const ChevronUp = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 6.5L5 3.5L8 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
